<!--
@component
LazyMount

Mount children only when the wrapper enters (or is near) the viewport. Off-screen
content stays as a fixed-height placeholder so the page's scrollbar/layout is
accurate, but the actual card components (with all their hover styles, shadows,
flags, etc.) never hydrate or paint until needed.

Why not `content-visibility: auto`?
  CSS `content-visibility: auto` implies `contain: paint`, which clips drop-
  shadows and overflowing children (e.g. our `.isLive` LiveBug, `.qualified`
  hard-shadow, hover halos). `LazyMount` avoids that entirely because the
  placeholder is just an empty div with reserved height — no shadows to clip —
  and once mounted, the row has no `contain` constraints.

Why not virtual scrolling?
  We want to keep the URL hash / find-in-page / Svelte transitions working on
  rows that have been mounted. `LazyMount` only delays initial render; once a
  row is mounted it stays mounted (incremental hydration), which is far simpler
  than a windowed virtualiser and matches how the existing in:fly transitions
  expect things to behave.

Refs #57.
-->

<script lang="ts">
  import { onMount, type Snippet } from 'svelte';

  type Props = {
    /**
     * CSS length reserved for the placeholder while children are not yet
     * mounted. Match this to the rendered card's height so the scrollbar
     * stays accurate.
     */
    height: string;
    /**
     * `IntersectionObserver` rootMargin. A generous default mounts rows just
     * before they scroll into view, avoiding a flash of empty space.
     * @default '400px 0px'
     */
    rootMargin?: string;
    /**
     * Optional snippet rendered inside the reserved space while the row is
     * not yet mounted. Typically a cheap skeleton/placeholder that matches
     * the final card's footprint so users see a loading state instead of
     * blank space during SSR/hydration and while scrolling toward off-
     * screen rows. Keep this content lightweight — it renders for every
     * row up front, so anything heavy (images, observers, derived state)
     * defeats the point of `LazyMount`.
     */
    placeholder?: Snippet;
    children: Snippet;
  };

  let {
    height,
    rootMargin = '400px 0px',
    placeholder,
    children,
  }: Props = $props();

  let el: HTMLDivElement;
  let mounted = $state(false);

  onMount(() => {
    // SSR / unsupported environments: render eagerly. Avoids a perma-blank
    // placeholder if `IntersectionObserver` isn't available.
    if (typeof IntersectionObserver === 'undefined') {
      mounted = true;
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            mounted = true;

            io.disconnect();
            return;
          }
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  });
</script>

<div
  bind:this={el}
  class="lazy-mount"
  style={mounted ? undefined : `min-height: ${height};`}
>
  {#if mounted}
    {@render children()}
  {:else if placeholder}
    {@render placeholder()}
  {/if}
</div>

<style lang="scss">
  .lazy-mount {
    // Hint the browser that this subtree won't affect its siblings'
    // layout/style, so it can short-circuit some recalc work even before the
    // observer fires. Safe to keep on after mount — the row's content lays
    // out independently of others either way.
    contain: style;
  }
</style>
